import { createContext, Dispatch, PropsWithChildren, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { preloadImage } from "utils/imageCache";
import {
    DEFAULT_CONVERSION_RATES,
    DEFAULT_CONVERSION_RATES_MODE,
    DEFAULT_DISPLAY_CURRENCY,
    autoConversionRatesForDisplay,
    type ConversionRatesMode,
} from "shared/money/balances";
import { fetchDollarBolsaReferenceRate } from "features/market";
import { getUserProfile, updateOnboardingCompleted as persistOnboardingCompleted } from "features/auth";
import { responseCache } from "shared/money/responseCache";
import { Capacitor } from "@capacitor/core";
import { apiPut, invalidateApiSession } from "shared/api/apiClient";

// -----------------------------------------------------------------------------
//  Locals
// -----------------------------------------------------------------------------
const COOKIE_USERNAME = "USERNAME";
const COOKIE_PICTURE  = "PICTURE";
const COOKIE_TOKEN    = "TOKEN";
const COOKIE_USER_ID  = "USER_ID";
const HEADER_FADE_DISTANCE = 100;
const REMEMBER_ME_DAYS = 7;
const BALANCES_VISIBLE_STORAGE_KEY = "balancesVisible";
const AUTO_FX_REFRESH_MS = 10 * 60 * 1000;
const defaultUserImage = "";

// -----------------------------------------------------------------------------
//  Types
// -----------------------------------------------------------------------------

export interface ActiveHeader {
    name: string;
    balanceLabel?: string;
    balanceColor?: "success" | "error";
    fadeDistance: number;
}

export interface UserSettings {
    displayCurrency: string;
    conversionRates: Record<string, number>;
    conversionRatesMode: ConversionRatesMode;
    /** Live bolsa USD/ARS used to build automatic rates. */
    usdArsReference?: number;
    conversionRatesUpdatedAt?: string;
    conversionRatesSource?: "dolar_bolsa" | "fallback";
    conversionRatesLoading?: boolean;
    conversionRatesError?: string | null;
}

export interface UserProfile {
    token: string;
    username: string;
    profilePicture: string;
    userId: string;
}

export interface UserContextValue {
    profile: UserProfile;
    activeHeader: ActiveHeader;
    setActiveHeader: Dispatch<SetStateAction<ActiveHeader>>;
    userSettings: UserSettings;
    setUserSettings: (settings: Partial<UserSettings>) => void;
    balancesVisible: boolean;
    setBalancesVisible: (visible: boolean) => void;
    onboardingCompleted: boolean;
    onboardingReady: boolean;
    setOnboardingCompleted: (completed: boolean) => Promise<boolean>;
    setupUser: (userName: string, pfp: string, token: string, userId: string, rememberMe?: boolean) => void;
    resetUser: () => void;
    updateUser: (userName: string, pfp: string) => void;
    isLogged: () => boolean;
}

type UserSessionValue = Pick<UserContextValue, "profile" | "setupUser" | "resetUser" | "updateUser" | "isLogged">;
type FinancialPreferencesValue = Pick<UserContextValue, "userSettings" | "setUserSettings">;
type FinancialVisibilityValue = Pick<UserContextValue, "balancesVisible" | "setBalancesVisible">;
type ActiveHeaderValue = Pick<UserContextValue, "activeHeader">;
type ActiveHeaderActions = Pick<UserContextValue, "setActiveHeader">;
type UserOnboardingValue = Pick<UserContextValue, "onboardingCompleted" | "onboardingReady" | "setOnboardingCompleted">;

const UserSessionContext = createContext<UserSessionValue | null>(null);
const FinancialPreferencesContext = createContext<FinancialPreferencesValue | null>(null);
const FinancialVisibilityContext = createContext<FinancialVisibilityValue | null>(null);
const ActiveHeaderContext = createContext<ActiveHeaderValue | null>(null);
const ActiveHeaderActionsContext = createContext<ActiveHeaderActions | null>(null);
const UserOnboardingContext = createContext<UserOnboardingValue | null>(null);

export function useUserSession(): UserSessionValue {
    const value = useContext(UserSessionContext);
    if (!value) throw new Error("useUserSession must be used within UserProvider");
    return value;
}

export function useFinancialPreferences(): FinancialPreferencesValue {
    const value = useContext(FinancialPreferencesContext);
    if (!value) throw new Error("useFinancialPreferences must be used within UserProvider");
    return value;
}

export function useFinancialVisibility(): FinancialVisibilityValue {
    const value = useContext(FinancialVisibilityContext);
    if (!value) throw new Error("useFinancialVisibility must be used within UserProvider");
    return value;
}

export function useActiveHeader(): ActiveHeaderValue {
    const value = useContext(ActiveHeaderContext);
    if (!value) throw new Error("useActiveHeader must be used within UserProvider");
    return value;
}

export function useSetActiveHeader(): ActiveHeaderActions {
    const value = useContext(ActiveHeaderActionsContext);
    if (!value) throw new Error("useSetActiveHeader must be used within UserProvider");
    return value;
}

export function useUserOnboarding(): UserOnboardingValue {
    const value = useContext(UserOnboardingContext);
    if (!value) throw new Error("useUserOnboarding must be used within UserProvider");
    return value;
}

// -----------------------------------------------------------------------------
//  Context
// -----------------------------------------------------------------------------

export const UserContext = createContext<UserContextValue>(
{
    profile: {
        username: "",
        profilePicture: "",
        userId: "",
        token: ""
    },
    activeHeader: {
        name: "home",
        fadeDistance: 100,
    },
    setActiveHeader: () => {},
    userSettings: {
        displayCurrency: DEFAULT_DISPLAY_CURRENCY,
        conversionRates: { ...DEFAULT_CONVERSION_RATES },
        conversionRatesMode: DEFAULT_CONVERSION_RATES_MODE,
        conversionRatesSource: "fallback",
    },
    setUserSettings: () => {},
    balancesVisible: true,
    setBalancesVisible: () => {},
    onboardingCompleted: false,
    onboardingReady: false,
    setOnboardingCompleted: async () => false,
    setupUser: () => {},
    resetUser: () => {},
    updateUser: () => {},
    isLogged: () => false,
});

function loadBalancesVisible(): boolean {
    try {
        const v = localStorage.getItem(BALANCES_VISIBLE_STORAGE_KEY);
        if (v === "false") return false;
        if (v === "true") return true;
    } catch (_) {}
    return true;
}

//! UserProvider wraps the app and exposes profile/header state + helpers.
export const UserProvider = ({ children }: PropsWithChildren) => {
    
    const usernameFromCookie = Cookies.get(COOKIE_USERNAME) ?? "Username";
    const pictureFromCookie  = Cookies.get(COOKIE_PICTURE)  ?? defaultUserImage;
    const userIdFromCookie   = Cookies.get(COOKIE_USER_ID)  ?? "";
    const tokenFromCookie    = Cookies.get(COOKIE_TOKEN)    ?? "";

    const [profile, setProfile] = useState<UserProfile>({
        username: usernameFromCookie,
        profilePicture: pictureFromCookie,
        userId: userIdFromCookie,
        token: tokenFromCookie
    });

    const [activeHeader, setActiveHeader] = useState<ActiveHeader>({
        name: "",
        fadeDistance: HEADER_FADE_DISTANCE
    });

    const [userSettings, setUserSettingsState] = useState<UserSettings>({
        displayCurrency: DEFAULT_DISPLAY_CURRENCY,
        conversionRates: { ...DEFAULT_CONVERSION_RATES },
        conversionRatesMode: DEFAULT_CONVERSION_RATES_MODE,
        conversionRatesSource: "fallback",
    });

    const [balancesVisible, setBalancesVisibleState] = useState<boolean>(() => loadBalancesVisible());
    const [onboardingCompleted, setOnboardingCompletedState] = useState(false);
    const [onboardingReady, setOnboardingReady] = useState(false);
    const setBalancesVisible = useCallback((visible: boolean) => {
        setBalancesVisibleState(visible);
        try {
            localStorage.setItem(BALANCES_VISIBLE_STORAGE_KEY, String(visible));
        } catch (_) {}
        if (Capacitor.isNativePlatform()) {
            const token = Cookies.get(COOKIE_TOKEN);
            if (token) {
                void apiPut("/mobile/notification-settings", { balancesVisible: visible }, token).catch(() => {});
            }
        }
    }, []);

    const setUserSettings = useCallback((settings: Partial<UserSettings>) => {
        setUserSettingsState((prev) => ({
            displayCurrency: settings.displayCurrency ?? prev.displayCurrency,
            conversionRates: settings.conversionRates != null ? { ...settings.conversionRates } : prev.conversionRates,
            conversionRatesMode: settings.conversionRatesMode ?? prev.conversionRatesMode,
            usdArsReference: settings.usdArsReference ?? prev.usdArsReference,
            conversionRatesUpdatedAt: settings.conversionRatesUpdatedAt ?? prev.conversionRatesUpdatedAt,
            conversionRatesSource: settings.conversionRatesSource ?? prev.conversionRatesSource,
            conversionRatesLoading: settings.conversionRatesLoading ?? prev.conversionRatesLoading,
            conversionRatesError: settings.conversionRatesError ?? prev.conversionRatesError,
        }));
    }, []);

    // Load user settings (displayCurrency, conversionRates) from backend on mount
    useEffect(() => {
        const { token, userId } = profile;
        if (!token || !userId) {
            setOnboardingCompletedState(false);
            setOnboardingReady(false);
            return;
        }

        let cancelled = false;
        setOnboardingReady(false);
        getUserProfile(userId, token).then((data) => {
            if (cancelled) return;
            if (!data) {
                setOnboardingReady(true);
                return;
            }
            if (data.displayCurrency != null || data.conversionRatesMode != null) {
                setUserSettingsState((prev) => {
                    const nextDisplayCurrency =
                        data.displayCurrency != null && data.displayCurrency !== ''
                            ? data.displayCurrency
                            : prev.displayCurrency;
                    return {
                        displayCurrency: nextDisplayCurrency,
                        conversionRates: prev.conversionRates,
                        conversionRatesMode: "auto",
                        conversionRatesSource: prev.conversionRatesSource,
                        usdArsReference: prev.usdArsReference,
                        conversionRatesUpdatedAt: prev.conversionRatesUpdatedAt,
                        conversionRatesLoading: prev.conversionRatesLoading,
                        conversionRatesError: prev.conversionRatesError,
                    };
                });
            }
            setOnboardingCompletedState(Boolean(data.onboardingCompleted));
            setOnboardingReady(true);
        }).catch(() => {
            if (cancelled) return;
            setOnboardingReady(true);
        });
        return () => { cancelled = true; };
        // Only run on mount / when credentials change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile.token, profile.userId]);

    useEffect(() => {
        if (!profile.token || !profile.userId) return;

        let cancelled = false;
        let timer: ReturnType<typeof setInterval> | undefined;

        const refreshAutoRates = async () => {
            setUserSettingsState((prev) => ({ ...prev, conversionRatesLoading: true, conversionRatesError: null }));
            try {
                const quote = await fetchDollarBolsaReferenceRate();
                const usdArs = Number(quote.venta);
                if (!Number.isFinite(usdArs) || usdArs <= 0) {
                    throw new Error("Invalid dollar bolsa reference rate");
                }
                if (cancelled) return;
                setUserSettingsState((prev) => ({
                    ...prev,
                    conversionRates: autoConversionRatesForDisplay(prev.displayCurrency, usdArs),
                    conversionRatesMode: "auto",
                    usdArsReference: usdArs,
                    conversionRatesUpdatedAt: quote.fecha || new Date().toISOString(),
                    conversionRatesSource: "dolar_bolsa",
                    conversionRatesLoading: false,
                    conversionRatesError: null,
                }));
            } catch (err) {
                if (cancelled) return;
                setUserSettingsState((prev) => ({
                    ...prev,
                    conversionRatesLoading: false,
                    conversionRatesError: err instanceof Error ? err.message : String(err),
                }));
            }
        };

        refreshAutoRates();
        timer = setInterval(refreshAutoRates, AUTO_FX_REFRESH_MS);

        return () => {
            cancelled = true;
            if (timer) clearInterval(timer);
        };
    }, [profile.token, profile.userId, userSettings.displayCurrency]);

    // Preload profile picture when it changes
    useEffect(() => {
        if (profile.profilePicture && profile.profilePicture !== defaultUserImage) {
            preloadImage(profile.profilePicture).catch(error => {
                console.warn('Failed to preload profile picture:', error);
            });
        }
    }, [profile.profilePicture]);

    // Preload default image on mount
    useEffect(() => {
        preloadImage(defaultUserImage).catch(() => {
            // Ignore errors for default image
        });
    }, []);

    // -----------------------------------------------------------------------------
    const setupUser = useCallback((userName: string, pfp: string, token: string, userId: string, rememberMe?: boolean): void => {
        invalidateApiSession();
        setProfile({
            username: userName,
            profilePicture: pfp,
            userId: userId,
            token: token
        });

        const cookieOptions = rememberMe ? { expires: REMEMBER_ME_DAYS } : {};
        Cookies.set(COOKIE_USERNAME, userName, cookieOptions);
        Cookies.set(COOKIE_PICTURE,  pfp, cookieOptions);
        Cookies.set(COOKIE_TOKEN,    token, cookieOptions);
        Cookies.set(COOKIE_USER_ID,  userId, cookieOptions);

        // Preload the profile picture
        if (pfp && pfp !== defaultUserImage) {
            preloadImage(pfp).catch(error => {
                console.warn('Failed to preload profile picture:', error);
            });
        }
    }, []);

    // -----------------------------------------------------------------------------
    const updateUser = useCallback((userName: string, pfp: string): void => {
        setProfile((prev) => ({
            username: userName,
            profilePicture: pfp,
            userId: prev.userId,
            token: prev.token
        }));

        Cookies.set(COOKIE_USERNAME, userName);
        Cookies.set(COOKIE_PICTURE,  pfp);

        // Preload the new profile picture
        if (pfp && pfp !== defaultUserImage) {
            preloadImage(pfp).catch(error => {
                console.warn('Failed to preload profile picture:', error);
            });
        }
    }, []);

    // -----------------------------------------------------------------------------
    const resetUser = useCallback((): void => {
        invalidateApiSession();
        const userIdToClear = profile.userId;
        if (userIdToClear) {
            responseCache.clearForUser(userIdToClear);
        }
        setProfile({
            username: "Username",
            profilePicture: defaultUserImage,
            userId: "",
            token: ""
        });
        setUserSettingsState({
            displayCurrency: DEFAULT_DISPLAY_CURRENCY,
            conversionRates: { ...DEFAULT_CONVERSION_RATES },
            conversionRatesMode: DEFAULT_CONVERSION_RATES_MODE,
            conversionRatesSource: "fallback",
        });
        setOnboardingCompletedState(false);
        setOnboardingReady(false);

        Cookies.remove(COOKIE_TOKEN);
        Cookies.remove(COOKIE_USER_ID);
        Cookies.set(COOKIE_USERNAME, "Username");
        Cookies.set(COOKIE_PICTURE,  defaultUserImage);
    }, [profile.userId]);

    // -----------------------------------------------------------------------------
    const isLogged = useCallback((): boolean => {
        return Boolean(Cookies.get(COOKIE_TOKEN));
    }, []);

    const setOnboardingCompleted = useCallback(async (completed: boolean): Promise<boolean> => {
        const previous = onboardingCompleted;
        setOnboardingCompletedState(completed);

        if (!profile.userId || !profile.token) {
            setOnboardingCompletedState(previous);
            return false;
        }

        try {
            await persistOnboardingCompleted(profile.userId, profile.token, completed);
            return true;
        } catch {
            setOnboardingCompletedState(previous);
            return false;
        }
    }, [onboardingCompleted, profile.userId, profile.token]);

    const value: UserContextValue = useMemo(() => ({
        profile,
        activeHeader,
        setActiveHeader,
        userSettings,
        setUserSettings,
        balancesVisible,
        setBalancesVisible,
        onboardingCompleted,
        onboardingReady,
        setOnboardingCompleted,
        setupUser,
        resetUser,
        updateUser,
        isLogged
    }), [
        profile,
        activeHeader,
        setActiveHeader,
        userSettings,
        setUserSettings,
        balancesVisible,
        setBalancesVisible,
        onboardingCompleted,
        onboardingReady,
        setOnboardingCompleted,
        setupUser,
        resetUser,
        updateUser,
        isLogged
    ]);

    const sessionValue = useMemo<UserSessionValue>(() => ({ profile, setupUser, resetUser, updateUser, isLogged }), [profile, setupUser, resetUser, updateUser, isLogged]);
    const preferencesValue = useMemo<FinancialPreferencesValue>(() => ({ userSettings, setUserSettings }), [userSettings, setUserSettings]);
    const visibilityValue = useMemo<FinancialVisibilityValue>(() => ({ balancesVisible, setBalancesVisible }), [balancesVisible, setBalancesVisible]);
    const headerValue = useMemo<ActiveHeaderValue>(() => ({ activeHeader }), [activeHeader]);
    const headerActions = useMemo<ActiveHeaderActions>(() => ({ setActiveHeader }), []);
    const onboardingValue = useMemo<UserOnboardingValue>(() => ({ onboardingCompleted, onboardingReady, setOnboardingCompleted }), [onboardingCompleted, onboardingReady, setOnboardingCompleted]);

    return (
        <UserContext.Provider value={value}>
            <UserSessionContext.Provider value={sessionValue}>
                <FinancialPreferencesContext.Provider value={preferencesValue}>
                    <FinancialVisibilityContext.Provider value={visibilityValue}>
                        <ActiveHeaderContext.Provider value={headerValue}>
                            <ActiveHeaderActionsContext.Provider value={headerActions}>
                                <UserOnboardingContext.Provider value={onboardingValue}>
                                    {children}
                                </UserOnboardingContext.Provider>
                            </ActiveHeaderActionsContext.Provider>
                        </ActiveHeaderContext.Provider>
                    </FinancialVisibilityContext.Provider>
                </FinancialPreferencesContext.Provider>
            </UserSessionContext.Provider>
        </UserContext.Provider>
    );
};
